/**
 * Time formatting utilities for converting 24-hour to 12-hour format
 * Centralizes all time formatting to ensure consistent AM/PM display
 */

/**
 * Converts 24-hour time format to 12-hour format with AM/PM
 * @param time24 - Time in 24-hour format (e.g., "14:30" or "14:30:00")
 * @returns Time in 12-hour format (e.g., "2:30 PM")
 */
export const formatTimeToAmPm = (time24: string): string => {
  if (!time24) return '';
  
  try {
    // Parse the time (assuming format HH:MM or HH:MM:SS)
    const [hours, minutes] = time24.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) return time24; // Return original if invalid
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return time24; // Return original if parsing fails
  }
};

/**
 * Formats a time range from start to end time
 * @param startTime - Start time in 24-hour format
 * @param endTime - End time in 24-hour format
 * @returns Formatted time range (e.g., "2:30 PM - 4:00 PM")
 */
export const formatTimeRange = (startTime: string, endTime: string): string => {
  const start = formatTimeToAmPm(startTime);
  const end = formatTimeToAmPm(endTime);
  
  if (!start && !end) return '';
  if (!end) return start;
  if (!start) return end;
  
  return `${start} - ${end}`;
};

/**
 * Formats schedule display with day and time
 * @param dayOfWeek - Day of the week
 * @param startTime - Start time in 24-hour format
 * @param endTime - End time in 24-hour format
 * @returns Formatted schedule (e.g., "Monday 2:30 PM - 4:00 PM")
 */
export const formatScheduleDisplay = (dayOfWeek: string, startTime: string, endTime?: string): string => {
  const timeRange = endTime ? formatTimeRange(startTime, endTime) : formatTimeToAmPm(startTime);
  return `${dayOfWeek} ${timeRange}`;
};